import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { IScene } from './IScene';
import { Cat } from '../entities/Cat';
import { sound } from '../utils/Audio';

export class MenuScene implements IScene {
  public container: Container;
  private cat: Cat;
  private onStartCallback: () => void;
  private animTime: number = 0;

  private titleContainer: Container;
  private startBtn: Container;
  private infoCard: Container;
  private featuresCard: Container;
  private bgGraphics: Graphics;

  private layoutWidth: number = 960;
  private layoutHeight: number = 680;

  constructor(highScore: number, onStart: () => void) {
    this.container = new Container();
    this.onStartCallback = onStart;

    this.bgGraphics = new Graphics();
    this.container.addChild(this.bgGraphics);

    this.titleContainer = new Container();
    this.infoCard = new Container();
    this.featuresCard = new Container();
    this.startBtn = new Container();

    this.container.addChild(this.titleContainer);
    this.container.addChild(this.infoCard);
    this.container.addChild(this.featuresCard);
    this.container.addChild(this.startBtn);

    this.cat = new Cat();
    this.container.addChild(this.cat.container);

    this.createUI(highScore);
  }

  private createUI(highScore: number) {
    // 1. Header / Title Block
    const titleStyle = new TextStyle({
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 38,
      fontWeight: 'bold',
      fill: '#f39c12',
      stroke: { color: '#ffffff', width: 3 },
      dropShadow: {
        alpha: 0.35,
        angle: Math.PI / 4,
        blur: 4,
        color: '#000000',
        distance: 4
      }
    });

    const titleText = new Text({
      text: '🐱 GOLDEN CHINCHILLA CHAOS',
      style: titleStyle
    });
    titleText.anchor.set(0.5, 0.5);
    this.titleContainer.addChild(titleText);

    const subStyle = new TextStyle({
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 17,
      fontWeight: '600',
      fill: '#fed39f'
    });
    const subText = new Text({
      text: 'Британский кот: Операция «Тыгыдык & Пакости»',
      style: subStyle
    });
    subText.anchor.set(0.5, 0.5);
    subText.position.set(0, 36);
    this.titleContainer.addChild(subText);

    // 2. Info / Lore Card
    const cardBg = new Graphics();
    cardBg.roundRect(-240, -38, 480, 76, 16);
    cardBg.fill({ color: 0x1e272e, alpha: 0.9 });
    cardBg.stroke({ width: 2, color: 0xf39c12, alpha: 0.3 });
    this.infoCard.addChild(cardBg);

    const descStyle = new TextStyle({
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 13.5,
      fill: '#ffffff',
      align: 'center',
      wordWrap: true,
      wordWrapWidth: 440,
      lineHeight: 20
    });
    const descText = new Text({
      text: `Хозяин ушел из дома! Твоя цель — разгромить квартиру до его возвращения.\n🏆 Рекорд хаоса: ${highScore}%`,
      style: descStyle
    });
    descText.anchor.set(0.5);
    this.infoCard.addChild(descText);

    // 3. Features Mini-Cards (Подсказки новых механик)
    const featBg = new Graphics();
    featBg.roundRect(-240, -28, 480, 56, 12);
    featBg.fill({ color: 0x111928, alpha: 0.8 });
    featBg.stroke({ width: 1.5, color: 0x374151 });
    this.featuresCard.addChild(featBg);

    const featStyle = new TextStyle({
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 12.5,
      fill: '#9ca3af',
      align: 'center'
    });
    const featText = new Text({
      text: '⚡ Shift: режим «Тыгыдык» (спринт)  •  🎾 Догони убегающий мячик за 10с!\n🐾 Space / E: драть шторы, мебель, сбрасывать посуду',
      style: featStyle
    });
    featText.anchor.set(0.5);
    this.featuresCard.addChild(featText);

    // 4. Start Button
    this.startBtn.eventMode = 'static';
    this.startBtn.cursor = 'pointer';

    const btnBg = new Graphics();
    btnBg.roundRect(-120, -28, 240, 56, 28);
    btnBg.fill({ color: 0xe67e22 });
    btnBg.stroke({ width: 3, color: 0xf39c12 });
    this.startBtn.addChild(btnBg);

    const btnStyle = new TextStyle({
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 20,
      fontWeight: 'bold',
      fill: '#ffffff',
      letterSpacing: 1
    });
    const btnText = new Text({
      text: 'НАЧАТЬ ХАОС 🐾',
      style: btnStyle
    });
    btnText.anchor.set(0.5);
    this.startBtn.addChild(btnText);

    this.startBtn.on('pointerover', () => {
      this.startBtn.scale.set(1.05);
    });
    this.startBtn.on('pointerout', () => {
      this.startBtn.scale.set(1.0);
    });
    this.startBtn.on('pointerdown', () => {
      sound.playMeow();
      this.onStartCallback();
    });
  }

  public update(delta: number) {
    this.animTime += delta * 0.05;
    this.cat.update(delta, false);

    // Subtle breathing/floating
    this.startBtn.position.y = this.layoutHeight * 0.88 + Math.sin(this.animTime * 2) * 3;
    this.titleContainer.position.y = this.layoutHeight * 0.12 + Math.sin(this.animTime) * 2;
  }

  public resize(width: number, height: number) {
    this.layoutWidth = 960;
    this.layoutHeight = 680;

    this.bgGraphics.clear();
    // Dark room atmosphere
    this.bgGraphics.rect(0, 0, this.layoutWidth, this.layoutHeight);
    this.bgGraphics.fill({ color: 0x0f172a });

    // Decorative cozy central carpet frame
    this.bgGraphics.roundRect(160, 40, 640, 600, 24);
    this.bgGraphics.fill({ color: 0x1e293b, alpha: 0.85 });
    this.bgGraphics.stroke({ width: 2, color: 0xf39c12, alpha: 0.25 });

    // Inner patterned frame
    this.bgGraphics.roundRect(180, 60, 600, 560, 16);
    this.bgGraphics.stroke({ width: 1, color: 0x475569, alpha: 0.4 });

    // Layout positions with generous breathing room
    this.titleContainer.position.set(this.layoutWidth / 2, this.layoutHeight * 0.14);

    // Cat in center
    this.cat.container.scale.set(1.7);
    this.cat.setPosition(this.layoutWidth / 2, this.layoutHeight * 0.38);
    this.cat.angle = 0;

    // Info card below cat
    this.infoCard.position.set(this.layoutWidth / 2, this.layoutHeight * 0.58);

    // Features mini-card
    this.featuresCard.position.set(this.layoutWidth / 2, this.layoutHeight * 0.72);

    // Start button
    this.startBtn.position.set(this.layoutWidth / 2, this.layoutHeight * 0.88);

    // Scale entire container nicely into window
    const scale = Math.min(width / this.layoutWidth, height / this.layoutHeight, 1.1);
    this.container.scale.set(scale);
    this.container.position.set(
      (width - this.layoutWidth * scale) / 2,
      (height - this.layoutHeight * scale) / 2
    );
  }

  public destroy() {
    this.container.destroy({ children: true });
  }
}
